import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const from = location.state?.from?.pathname || "/admin";

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Signed in");
      navigate(from, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not sign in";
      setErr(typeof msg === "string" ? msg : "Could not sign in");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="login-page" className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <header className="border-b border-[#E8E3DD]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="login-back" className="text-sm text-[#5C4E46] hover:text-[#2C1E16]">
            ← Back to site
          </Link>
          <Logo testId="login-logo" />
          <span className="w-20" />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.18em] text-[#8D99AE] mb-4">Admin</div>
          <h1 className="font-display text-4xl text-[#2C1E16] leading-tight">Sign in to your dashboard</h1>
          <p className="mt-3 text-[#5C4E46]">
            Manage pop-ups, view submissions, and keep the city updated.
          </p>

          <form
            data-testid="login-form"
            onSubmit={onSubmit}
            className="mt-10 bg-white border border-[#E8E3DD] rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-2">Email</span>
              <div className="relative">
                <Mail size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8D99AE]" />
                <input
                  data-testid="login-input-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="hello@memphiscoffee.co"
                  className="w-full pl-7 pr-0 py-3 bg-transparent border-0 border-b border-[#E8E3DD] focus:border-[#2C1E16] outline-none"
                  autoComplete="email"
                />
              </div>
            </label>

            <label className="block mt-6">
              <span className="block text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-2">Password</span>
              <div className="relative">
                <Lock size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8D99AE]" />
                <input
                  data-testid="login-input-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-7 pr-0 py-3 bg-transparent border-0 border-b border-[#E8E3DD] focus:border-[#2C1E16] outline-none"
                  autoComplete="current-password"
                />
              </div>
            </label>

            {err && (
              <p data-testid="login-error" className="mt-5 text-sm text-[#B33A3A]">{err}</p>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] disabled:opacity-60 transition-colors rounded-full px-7 py-3.5 font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" strokeWidth={1.6} />
                  Signing in…
                </>
              ) : (
                <>Sign in</>
              )}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
